import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../context/I18nContext";
import StudentShell from "../components/StudentShell/StudentShell";
import Pagination from "../components/Pagination/Pagination";
import * as authService from "../services/auth";
import * as classesService from "../services/classes";
import * as lessonsService from "../services/lessons";
import * as studentsService from "../services/students";
import * as usersService from "../services/users";
import { getAvatarUrl, getInitials } from "../utils/avatar";
import { dedupeTeacherSessions } from "../utils/teacherSessionDedupe";
import { materializeLessonForReference } from "../utils/lessonOccurrences";
import "./Profile.css";

const ROLE_LABEL_KEYS = {
  admin: "profile.roles.adminFull",
  teacher: "profile.roles.teacherFull",
  parent: "profile.roles.parentFull",
  student: "profile.roles.studentFull",
};

const ROLE_OPTIONS = [
  { value: "admin", labelKey: "profile.roles.adminShort" },
  { value: "teacher", labelKey: "profile.roles.teacherShort" },
  { value: "parent", labelKey: "profile.roles.parentShort" },
];

const MANAGED_ROLES = new Set(ROLE_OPTIONS.map((option) => option.value));

const EMPTY_STUDENT = {
  first_name: "",
  last_name: "",
  date_of_birth: "",
  notes: "",
};

const EMPTY_CLASS_FORM = {
  name: "",
  student_ids: [],
};

function roleBadgeClass(role) {
  if (!role) {
    return "profile-status";
  }
  return `profile-status profile-status--${role}`;
}

function formatDate(value, locale) {
  if (!value) {
    return "-";
  }
  try {
    return new Date(value).toLocaleDateString(locale);
  } catch (error) {
    return value;
  }
}

export default function Profile() {
  const { user, accessToken, updateUser, activeStudentId, effectiveRole } =
    useAuth();
  const { t, language } = useTranslation();

  const locale = useMemo(
    () => (language === "ar" ? "ar-TN" : language || "en"),
    [language],
  );

  const listSeparator = useMemo(
    () => (language === "ar" ? "، " : ", "),
    [language],
  );

  const roleLabel = useCallback(
    (role, variant = "full") => {
      if (!role) return "";
      if (variant === "short") {
        const found = ROLE_OPTIONS.find((opt) => opt.value === role);
        return found?.labelKey ? t(found.labelKey) : String(role);
      }
      const key = ROLE_LABEL_KEYS[role];
      return key ? t(key) : String(role);
    },
    [t],
  );
  const [profileForm, setProfileForm] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
  });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMessage, setProfileMessage] = useState(null);
  const [profileError, setProfileError] = useState(null);
  const [avatarUploading, setAvatarUploading] = useState(false);

  const isParent = user?.role === "parent";
  const isTeacher = user?.role === "teacher";
  const isAdmin = user?.role === "admin";

  const viewingStudentProfile = isParent && Boolean(activeStudentId);

  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [studentError, setStudentError] = useState(null);
  const [studentForm, setStudentForm] = useState(EMPTY_STUDENT);
  const [editingStudentId, setEditingStudentId] = useState(null);
  const [studentSaving, setStudentSaving] = useState(false);

  const [lessons, setLessons] = useState([]);
  const [lessonsLoading, setLessonsLoading] = useState(false);

  const sessionLessons = useMemo(
    () => (isTeacher ? dedupeTeacherSessions(lessons) : lessons),
    [isTeacher, lessons],
  );

  const [classGroups, setClassGroups] = useState([]);
  const [classesLoading, setClassesLoading] = useState(false);
  const [classesError, setClassesError] = useState(null);
  const [classForm, setClassForm] = useState(EMPTY_CLASS_FORM);
  const [editingClassId, setEditingClassId] = useState(null);
  const [classSaving, setClassSaving] = useState(false);

  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState(null);
  const [usersPage, setUsersPage] = useState(1);
  const [usersTotalPages, setUsersTotalPages] = useState(1);
  const [usersCount, setUsersCount] = useState(null);
  const [roleFilter, setRoleFilter] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const scrollToPersonalDetails = useCallback(() => {
    const target = document.getElementById("profile-first-name");
    if (!target) return;
    target.scrollIntoView({ behavior: "smooth", block: "center" });
    target.focus({ preventScroll: true });
  }, []);

  const avatarUrl = useMemo(() => getAvatarUrl(user), [user]);

  const profileProgress = useMemo(() => {
    const checks = [
      Boolean(user?.email),
      Boolean((profileForm.firstName || "").trim()),
      Boolean((profileForm.lastName || "").trim()),
      Boolean(avatarUrl),
    ];
    const filled = checks.filter(Boolean).length;
    const total = checks.length;
    const percent = Math.round((filled / total) * 100);
    return { filled, total, percent };
  }, [user?.email, profileForm.firstName, profileForm.lastName, avatarUrl]);

  useEffect(() => {
    if (user) {
      setProfileForm({
        firstName: user.first_name || "",
        lastName: user.last_name || "",
        phoneNumber: user.phone_number || "",
      });
    }
  }, [user]);

  const handleProfileSubmit = async (event) => {
    event.preventDefault();
    if (!accessToken) {
      return;
    }
    setProfileSaving(true);
    setProfileMessage(null);
    setProfileError(null);
    try {
      const payload = {
        first_name: profileForm.firstName.trim(),
        last_name: profileForm.lastName.trim(),
        phone_number: (profileForm.phoneNumber || "").trim(),
      };
      const result = await authService.updateProfile(accessToken, payload);
      updateUser(result.user);
      setProfileMessage(t("profile.messages.profileUpdated"));
    } catch (error) {
      setProfileError(error.message);
    } finally {
      setProfileSaving(false);
    }
  };

  const handleAvatarChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !accessToken) return;

    setAvatarUploading(true);
    setProfileMessage(null);
    setProfileError(null);
    try {
      const result = await authService.uploadAvatar(accessToken, file);
      updateUser(result.user);
      setProfileMessage(t("profile.messages.avatarUpdated"));
    } catch (error) {
      setProfileError(error.message);
    } finally {
      setAvatarUploading(false);
      event.target.value = "";
    }
  };

  const loadStudents = useCallback(async () => {
    if (!isParent || !accessToken) {
      return;
    }
    setStudentsLoading(true);
    setStudentError(null);
    try {
      const payload = await studentsService.fetchStudents({ accessToken });
      setStudents(payload.students || []);
    } catch (error) {
      setStudentError(error.message);
    } finally {
      setStudentsLoading(false);
    }
  }, [accessToken, isParent]);

  useEffect(() => {
    loadStudents();
  }, [loadStudents]);

  const viewedStudent = useMemo(() => {
    if (!viewingStudentProfile) return null;
    return (
      students.find((s) => String(s.id) === String(activeStudentId)) || null
    );
  }, [viewingStudentProfile, students, activeStudentId]);

  const viewedStudentName = useMemo(() => {
    if (!viewedStudent) return "";
    return (
      (
        viewedStudent.full_name ||
        `${viewedStudent.first_name || ""} ${viewedStudent.last_name || ""}`
      ).trim() || "—"
    );
  }, [viewedStudent]);

  const viewedStudentInitials = useMemo(() => {
    const name = String(viewedStudentName || "").trim();
    if (!name || name === "—") return "";
    const parts = name.split(/\s+/).filter(Boolean);
    if (!parts.length) return name.slice(0, 2).toUpperCase();
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }, [viewedStudentName]);

  const loadLessons = useCallback(async () => {
    if (!accessToken || (!isTeacher && !isParent)) {
      return;
    }
    setLessonsLoading(true);
    try {
      const payload = await lessonsService.fetchLessons({ accessToken });
      setLessons(payload.lessons || []);
    } catch (error) {
      setLessons([]);
    } finally {
      setLessonsLoading(false);
    }
  }, [accessToken, isTeacher, isParent]);

  useEffect(() => {
    loadLessons();
  }, [loadLessons]);

  const loadClasses = useCallback(async () => {
    if (!accessToken || !isTeacher) {
      return;
    }
    setClassesLoading(true);
    setClassesError(null);
    try {
      const payload = await classesService.fetchClasses({ accessToken });
      setClassGroups(payload.classes || []);
    } catch (error) {
      setClassesError(error.message);
      setClassGroups([]);
    } finally {
      setClassesLoading(false);
    }
  }, [accessToken, isTeacher]);

  useEffect(() => {
    loadClasses();
  }, [loadClasses]);

  const loadUsers = useCallback(async () => {
    if (!isAdmin || !accessToken) {
      return;
    }
    setUsersLoading(true);
    setUsersError(null);
    try {
      const payload = await usersService.fetchUsers({
        accessToken,
        role: roleFilter,
        search: searchTerm,
        page: usersPage,
      });
      setUsers(payload.users || []);
      setUsersPage(payload.page || usersPage || 1);
      setUsersTotalPages(payload.total_pages || 1);
      setUsersCount(
        typeof payload.count === "number"
          ? payload.count
          : payload.count
            ? Number(payload.count)
            : null,
      );
    } catch (error) {
      setUsersError(error.message);
    } finally {
      setUsersLoading(false);
    }
  }, [accessToken, isAdmin, roleFilter, searchTerm, usersPage]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleStudentFieldChange = (event) => {
    const { name, value } = event.target;
    setStudentForm((current) => ({ ...current, [name]: value }));
  };

  const resetStudentForm = () => {
    setStudentForm(EMPTY_STUDENT);
    setEditingStudentId(null);
  };

  const handleStudentSubmit = async (event) => {
    event.preventDefault();
    if (!accessToken) {
      return;
    }
    setStudentSaving(true);
    setStudentError(null);
    try {
      const payload = {
        first_name: studentForm.first_name.trim(),
        last_name: studentForm.last_name.trim(),
        date_of_birth: studentForm.date_of_birth || null,
        notes: studentForm.notes.trim(),
      };
      if (!payload.first_name) {
        throw new Error(t("profile.students.errors.firstNameRequired"));
      }
      if (editingStudentId) {
        const updated = await studentsService.updateStudent(
          accessToken,
          editingStudentId,
          payload,
        );
        setStudents((list) =>
          list.map((student) =>
            student.id === editingStudentId
              ? { ...student, ...updated }
              : student,
          ),
        );
      } else {
        const created = await studentsService.createStudent(
          accessToken,
          payload,
        );
        setStudents((list) => [created, ...list]);
      }
      resetStudentForm();
    } catch (error) {
      setStudentError(error.message);
    } finally {
      setStudentSaving(false);
    }
  };

  const handleStudentEdit = (student) => {
    setStudentForm({
      first_name: student.first_name,
      last_name: student.last_name,
      date_of_birth: student.date_of_birth || "",
      notes: student.notes || "",
    });
    setEditingStudentId(student.id);
  };

  const handleStudentDelete = async (studentId) => {
    if (!accessToken) {
      return;
    }
    const confirmed = window.confirm(t("profile.students.confirmDelete"));
    if (!confirmed) {
      return;
    }
    try {
      await studentsService.deleteStudent(accessToken, studentId);
      setStudents((list) => list.filter((student) => student.id !== studentId));
      if (editingStudentId === studentId) {
        resetStudentForm();
      }
    } catch (error) {
      setStudentError(error.message);
    }
  };

  const teacherRosterStudents = useMemo(() => {
    if (!isTeacher) {
      return [];
    }
    const map = new Map();
    (lessons || []).forEach((lesson) => {
      const student = lesson?.student;
      if (!student?.id) return;
      if (!map.has(student.id)) {
        map.set(student.id, student);
      }
    });
    return Array.from(map.values()).sort((a, b) =>
      String(a.full_name || "").localeCompare(
        String(b.full_name || ""),
        language === "ar" ? "ar" : language || "en",
        { sensitivity: "base" },
      ),
    );
  }, [lessons, isTeacher, language]);

  const resetClassForm = () => {
    setClassForm(EMPTY_CLASS_FORM);
    setEditingClassId(null);
  };

  const toggleClassStudent = (studentId) => {
    setClassForm((current) => {
      const id = Number(studentId);
      if (!Number.isInteger(id) || id <= 0) {
        return current;
      }
      const set = new Set((current.student_ids || []).map((v) => Number(v)));
      if (set.has(id)) {
        set.delete(id);
      } else {
        set.add(id);
      }
      return { ...current, student_ids: Array.from(set) };
    });
  };

  const handleClassSubmit = async (event) => {
    event.preventDefault();
    if (!accessToken) return;

    const name = (classForm.name || "").trim();
    if (!name) {
      setClassesError(t("profile.classes.errors.nameRequired"));
      return;
    }

    setClassSaving(true);
    setClassesError(null);
    try {
      const payload = {
        name,
        student_ids: classForm.student_ids || [],
      };

      if (editingClassId) {
        const updated = await classesService.updateClassGroup(
          accessToken,
          editingClassId,
          payload,
        );
        setClassGroups((list) =>
          list.map((item) => (item.id === editingClassId ? updated : item)),
        );
      } else {
        const created = await classesService.createClassGroup(
          accessToken,
          payload,
        );
        setClassGroups((list) => [created, ...(list || [])]);
      }
      resetClassForm();
    } catch (error) {
      setClassesError(error.message);
    } finally {
      setClassSaving(false);
    }
  };

  const handleClassEdit = (group) => {
    setEditingClassId(group.id);
    setClassForm({
      name: group.name || "",
      student_ids: Array.isArray(group.student_ids) ? group.student_ids : [],
    });
  };

  const handleClassDelete = async (groupId) => {
    if (!accessToken) return;
    const confirmed = window.confirm(t("profile.classes.confirmDelete"));
    if (!confirmed) return;

    try {
      await classesService.deleteClassGroup(accessToken, groupId);
      setClassGroups((list) => (list || []).filter((g) => g.id !== groupId));
      if (editingClassId === groupId) {
        resetClassForm();
      }
    } catch (error) {
      setClassesError(error.message);
    }
  };

  const upcomingLessons = useMemo(() => {
    const referenceDate = new Date();
    const referenceTs = referenceDate.getTime();

    return (sessionLessons || [])
      .map((lesson) => {
        const materialized = materializeLessonForReference(
          lesson,
          referenceDate,
        );
        if (!materialized?.scheduled_at) return null;

        const startTs = new Date(materialized.scheduled_at).getTime();
        if (!Number.isFinite(startTs)) return null;

        const durationMinutes = Number(materialized.duration_minutes) || 60;
        const endTs = startTs + durationMinutes * 60 * 1000;
        if (endTs < referenceTs) return null;

        return { lesson: materialized, startTs };
      })
      .filter(Boolean)
      .sort((a, b) => a.startTs - b.startTs)
      .slice(0, 5)
      .map((item) => item.lesson);
  }, [sessionLessons]);

  const lessonTotals = useMemo(() => {
    const completed = sessionLessons.filter(
      (lesson) => lesson.status === "completed",
    ).length;
    const cancelled = sessionLessons.filter(
      (lesson) => lesson.status === "cancelled",
    ).length;
    return {
      total: sessionLessons.length,
      completed,
      cancelled,
    };
  }, [sessionLessons]);

  const handleRoleUpdate = async (userId, role) => {
    if (!accessToken) {
      return;
    }
    try {
      const result = await usersService.updateUserRole({
        accessToken,
        userId,
        role,
      });
      setUsers((list) =>
        list.map((item) => (item.id === userId ? result.user : item)),
      );
      if (user && user.id === userId) {
        updateUser(result.user);
      }
    } catch (error) {
      setUsersError(error.message);
    }
  };

  const visibleUsers = useMemo(() => {
    const deduped = new Map();
    for (const item of users || []) {
      if (!item || item.id == null) continue;
      if (!item.email) continue;
      if (!MANAGED_ROLES.has(item.role)) continue;
      if (item.is_active === false) continue;
      deduped.set(item.id, item);
    }

    const list = Array.from(deduped.values());
    if (!searchTerm) {
      return list;
    }
    const term = searchTerm.toLowerCase();
    return list.filter(
      (item) =>
        item.email.toLowerCase().includes(term) ||
        (item.full_name || "").toLowerCase().includes(term),
    );
  }, [users, searchTerm]);

  const upcomingOverviewLessons = useMemo(() => {
    if (!isTeacher && !isParent) {
      return [];
    }
    return upcomingLessons.slice(0, 2);
  }, [upcomingLessons, isTeacher, isParent]);

  const roleCounts = useMemo(() => {
    return visibleUsers.reduce(
      (accumulator, item) => ({
        ...accumulator,
        [item.role]: (accumulator[item.role] || 0) + 1,
      }),
      {},
    );
  }, [visibleUsers]);

  const page = (
    <div className="profile-page">
      <div className="profile-shell">
        <section className="profile-overview">
          <article className="profile-overview__main">
            <div className="profile-overview__banner" aria-hidden="true" />

            <header className="profile-overview__head">
              <div
                className="profile-avatar profile-overview__avatar"
                aria-hidden="true"
              >
                {viewingStudentProfile ? (
                  viewedStudentInitials || "•"
                ) : avatarUrl ? (
                  <img src={avatarUrl} alt="" loading="lazy" />
                ) : (
                  getInitials(user)
                )}
              </div>

              <div className="profile-overview__identity">
                <h2 className="profile-overview__name">
                  {viewingStudentProfile
                    ? studentsLoading
                      ? t("profile.common.loadingData")
                      : viewedStudentName || "—"
                    : (
                        user?.full_name ||
                        `${user?.first_name || ""} ${user?.last_name || ""}`
                      ).trim() ||
                      user?.email ||
                      "—"}
                </h2>
                <div className="profile-overview__meta">
                  {viewingStudentProfile ? (
                    <span className={roleBadgeClass("student")}>
                      {roleLabel("student")}
                    </span>
                  ) : user?.role ? (
                    <span className={roleBadgeClass(user.role)}>
                      {roleLabel(user.role) || user.role}
                    </span>
                  ) : null}
                  {!viewingStudentProfile && user?.email ? (
                    <span className="profile-overview__email">
                      {user.email}
                    </span>
                  ) : null}
                </div>
              </div>
            </header>

            <div className="profile-overview__about">
              <h3 className="profile-overview__sectionTitle">
                {t("profile.overview.aboutTitle")}
              </h3>
              <p className="profile-muted">
                {viewingStudentProfile
                  ? t("profile.overview.aboutTextStudent")
                  : t("profile.overview.aboutTextUser")}
              </p>
            </div>

            {!viewingStudentProfile ? (
              <div className="profile-overview__actions">
                <label
                  className="profile-button profile-button--ghost"
                  aria-disabled={avatarUploading}
                >
                  {avatarUploading
                    ? t("profile.overview.uploadingPhoto")
                    : t("profile.overview.changePhoto")}
                  <input
                    className="profile-file-input"
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarChange}
                    disabled={avatarUploading}
                  />
                </label>
              </div>
            ) : null}
          </article>

          <aside className="profile-overview__side">
            {viewingStudentProfile ? (
              <div className="profile-sideCard">
                <div className="profile-sideCard__title">
                  {t("profile.studentDetails.title")}
                </div>
                {studentsLoading ? (
                  <p className="profile-muted">
                    {t("profile.common.loadingData")}
                  </p>
                ) : viewedStudent ? (
                  <>
                    <p className="profile-muted">
                      {t("profile.common.idLabel", { id: viewedStudent.id })}
                    </p>
                    {viewedStudent.date_of_birth ? (
                      <p className="profile-muted">
                        {t("profile.common.dobLabel", {
                          date: formatDate(viewedStudent.date_of_birth, locale),
                        })}
                      </p>
                    ) : null}
                    {viewedStudent.notes ? (
                      <p className="profile-muted">{viewedStudent.notes}</p>
                    ) : null}
                  </>
                ) : (
                  <p className="profile-muted">
                    {t("profile.studentDetails.notFound")}
                  </p>
                )}
              </div>
            ) : (
              <div className="profile-sideCard">
                <div className="profile-progress">
                  <div
                    className="profile-progress__ring"
                    style={{ "--progress": `${profileProgress.percent}%` }}
                    aria-hidden="true"
                  >
                    <div className="profile-progress__value">
                      {profileProgress.percent}%
                    </div>
                  </div>
                  <div className="profile-progress__copy">
                    <div className="profile-progress__title">
                      {t("profile.progress.title")}
                    </div>
                    <div className="profile-progress__subtitle">
                      {t("profile.progress.subtitle")}
                    </div>
                    <button
                      type="button"
                      className="profile-button profile-button--ghost"
                      onClick={scrollToPersonalDetails}
                    >
                      {t("profile.progress.completeProfile")}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {!viewingStudentProfile && (isTeacher || isParent) && (
              <div className="profile-sideCard">
                <div className="profile-sideCard__title">
                  {t("profile.overview.upcomingLessons")}
                </div>
                {lessonsLoading ? (
                  <p className="profile-muted">
                    {t("profile.common.loadingData")}
                  </p>
                ) : upcomingOverviewLessons.length ? (
                  <div className="profile-sideList">
                    {upcomingOverviewLessons.map((lesson) => (
                      <div className="profile-sideList__item" key={lesson.id}>
                        <div className="profile-sideList__name">
                          {lesson.title}
                        </div>
                        <div className="profile-sideList__meta">
                          {new Date(lesson.scheduled_at).toLocaleString(locale)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="profile-muted">
                    {t("profile.overview.noUpcomingLessons")}
                  </p>
                )}
              </div>
            )}
          </aside>
        </section>

        <section className="profile-section profile-details">
          <header className="profile-section__head">
            <div>
              <h2>{t("profile.personal.title")}</h2>
              <p className="profile-muted">
                {viewingStudentProfile
                  ? t("profile.personal.subtitleStudent")
                  : t("profile.personal.subtitleUser")}
              </p>
            </div>
            {!viewingStudentProfile ? (
              <button
                type="button"
                className="profile-button profile-button--ghost profile-details__edit"
                onClick={scrollToPersonalDetails}
              >
                {t("profile.personal.edit")}
              </button>
            ) : null}
          </header>

          <div
            className="profile-details__table"
            role="group"
            aria-label={t("profile.personal.accountDetailsAria")}
          >
            <div className="profile-details__cell">
              <div className="profile-details__label">
                {t("profile.fields.firstName")}
              </div>
              <div className="profile-details__value">
                {viewingStudentProfile
                  ? viewedStudent?.first_name || "—"
                  : profileForm.firstName || "—"}
              </div>
            </div>
            <div className="profile-details__cell">
              <div className="profile-details__label">
                {t("profile.fields.lastName")}
              </div>
              <div className="profile-details__value">
                {viewingStudentProfile
                  ? viewedStudent?.last_name || "—"
                  : profileForm.lastName || "—"}
              </div>
            </div>
            <div className="profile-details__cell">
              <div className="profile-details__label">
                {t("profile.fields.role")}
              </div>
              <div className="profile-details__value">
                {viewingStudentProfile
                  ? roleLabel("student")
                  : roleLabel(user?.role) || user?.role || "—"}
              </div>
            </div>
            <div className="profile-details__cell">
              <div className="profile-details__label">
                {t("profile.fields.email")}
              </div>
              <div className="profile-details__value">
                {viewingStudentProfile ? "—" : user?.email || "—"}
              </div>
            </div>
            {viewingStudentProfile ? (
              <div className="profile-details__cell">
                <div className="profile-details__label">
                  {t("profile.fields.dob")}
                </div>
                <div className="profile-details__value">
                  {viewedStudent?.date_of_birth
                    ? formatDate(viewedStudent.date_of_birth, locale)
                    : "—"}
                </div>
              </div>
            ) : null}
            <div className="profile-details__cell">
              <div className="profile-details__label">
                {t("profile.fields.completion")}
              </div>
              <div className="profile-details__value">
                {viewingStudentProfile ? "—" : `${profileProgress.percent}%`}
              </div>
            </div>
            <div className="profile-details__cell">
              <div className="profile-details__label">
                {t("profile.fields.status")}
              </div>
              <div className="profile-details__value">
                <span
                  className={roleBadgeClass(
                    viewingStudentProfile ? "student" : user?.role,
                  )}
                >
                  {viewingStudentProfile
                    ? roleLabel("student")
                    : roleLabel(user?.role) || user?.role || "—"}
                </span>
              </div>
            </div>
          </div>

          {!viewingStudentProfile ? (
            <form className="profile-form" onSubmit={handleProfileSubmit}>
              <div className="profile-grid">
                <div className="profile-field">
                  <label htmlFor="profile-first-name">
                    {t("profile.fields.firstName")}
                  </label>
                  <input
                    id="profile-first-name"
                    name="firstName"
                    type="text"
                    value={profileForm.firstName}
                    onChange={(event) =>
                      setProfileForm((current) => ({
                        ...current,
                        firstName: event.target.value,
                      }))
                    }
                    placeholder={t("profile.placeholders.firstName")}
                    disabled={profileSaving}
                  />
                </div>
                <div className="profile-field">
                  <label htmlFor="profile-last-name">
                    {t("profile.fields.lastName")}
                  </label>
                  <input
                    id="profile-last-name"
                    name="lastName"
                    type="text"
                    value={profileForm.lastName}
                    onChange={(event) =>
                      setProfileForm((current) => ({
                        ...current,
                        lastName: event.target.value,
                      }))
                    }
                    placeholder={t("profile.placeholders.lastName")}
                    disabled={profileSaving}
                  />
                </div>
                <div className="profile-field">
                  <label htmlFor="profile-phone">
                    {t("profile.fields.phone")}
                  </label>
                  <input
                    id="profile-phone"
                    name="phoneNumber"
                    type="tel"
                    value={profileForm.phoneNumber}
                    onChange={(event) =>
                      setProfileForm((current) => ({
                        ...current,
                        phoneNumber: event.target.value,
                      }))
                    }
                    placeholder={t("profile.placeholders.phone")}
                    disabled={profileSaving}
                    autoComplete="tel"
                    inputMode="tel"
                  />
                </div>
              </div>

              <div className="profile-actions profile-actions--inline">
                <button
                  type="submit"
                  className="btn primary"
                  disabled={profileSaving}
                >
                  {profileSaving
                    ? t("profile.actions.saving")
                    : t("profile.actions.saveChanges")}
                </button>
              </div>

              {profileMessage && (
                <p className="profile-info">{profileMessage}</p>
              )}
              {profileError && <p className="profile-error">{profileError}</p>}
            </form>
          ) : null}
        </section>

        {!viewingStudentProfile && isParent && (
          <section className="profile-section profile-students">
            <header className="profile-section__head">
              <div>
                <h2>{t("profile.students.title")}</h2>
                <p className="profile-muted">
                  {t("profile.students.subtitle")}
                </p>
              </div>
            </header>

            <form className="profile-form" onSubmit={handleStudentSubmit}>
              <div className="profile-grid">
                <div className="profile-field">
                  <label htmlFor="student-first-name">
                    {t("profile.fields.firstName")}
                  </label>
                  <input
                    id="student-first-name"
                    name="first_name"
                    type="text"
                    value={studentForm.first_name}
                    onChange={handleStudentFieldChange}
                    placeholder={t("profile.students.placeholders.firstName")}
                    disabled={studentSaving}
                    required
                  />
                </div>
                <div className="profile-field">
                  <label htmlFor="student-last-name">
                    {t("profile.fields.lastName")}
                  </label>
                  <input
                    id="student-last-name"
                    name="last_name"
                    type="text"
                    value={studentForm.last_name}
                    onChange={handleStudentFieldChange}
                    placeholder={t("profile.students.placeholders.lastName")}
                    disabled={studentSaving}
                  />
                </div>
                <div className="profile-field">
                  <label htmlFor="student-dob">{t("profile.fields.dob")}</label>
                  <input
                    id="student-dob"
                    name="date_of_birth"
                    type="date"
                    value={studentForm.date_of_birth}
                    onChange={handleStudentFieldChange}
                    disabled={studentSaving}
                  />
                </div>
              </div>
              <div className="profile-field">
                <label htmlFor="student-notes">
                  {t("profile.fields.notes")}
                </label>
                <textarea
                  id="student-notes"
                  name="notes"
                  value={studentForm.notes}
                  onChange={handleStudentFieldChange}
                  placeholder={t("profile.students.placeholders.notes")}
                  disabled={studentSaving}
                />
              </div>
              {studentError && <p className="profile-error">{studentError}</p>}
              <div className="profile-actions">
                <button
                  type="submit"
                  className="btn primary"
                  disabled={studentSaving}
                >
                  {studentSaving
                    ? t("profile.actions.saving")
                    : editingStudentId
                      ? t("profile.students.actions.update")
                      : t("profile.students.actions.add")}
                </button>
                {editingStudentId && (
                  <button
                    type="button"
                    className="profile-button profile-button--ghost"
                    onClick={resetStudentForm}
                    disabled={studentSaving}
                  >
                    {t("profile.actions.cancelEdit")}
                  </button>
                )}
              </div>
            </form>

            {studentsLoading ? (
              <p className="profile-muted">{t("profile.students.loading")}</p>
            ) : students.length ? (
              <div className="profile-grid">
                {students.map((student) => (
                  <article className="profile-card" key={student.id}>
                    <h3 className="profile-card__title">{student.full_name}</h3>
                    <p className="profile-muted">
                      {t("profile.common.idLabel", { id: student.id })}
                    </p>
                    {student.date_of_birth && (
                      <p className="profile-muted">
                        {t("profile.common.dobLabel", {
                          date: formatDate(student.date_of_birth, locale),
                        })}
                      </p>
                    )}
                    {student.notes && (
                      <p className="profile-muted">{student.notes}</p>
                    )}
                    <div className="profile-actions">
                      <button
                        type="button"
                        className="profile-button profile-button--ghost"
                        onClick={() => handleStudentEdit(student)}
                      >
                        {t("profile.actions.edit")}
                      </button>
                      <button
                        type="button"
                        className="profile-button profile-button--danger"
                        onClick={() => handleStudentDelete(student.id)}
                      >
                        {t("profile.actions.delete")}
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <p className="profile-empty">{t("profile.students.empty")}</p>
            )}
          </section>
        )}

        {!viewingStudentProfile && isTeacher && (
          <section className="profile-section profile-classes">
            <header className="profile-section__head">
              <div>
                <h2>{t("profile.classes.title")}</h2>
                <p className="profile-muted">{t("profile.classes.subtitle")}</p>
              </div>
            </header>

            <form className="profile-form" onSubmit={handleClassSubmit}>
              <div className="profile-grid">
                <div className="profile-field">
                  <label htmlFor="class-name">
                    {t("profile.classes.fields.name")}
                  </label>
                  <input
                    id="class-name"
                    type="text"
                    value={classForm.name}
                    onChange={(e) =>
                      setClassForm((current) => ({
                        ...current,
                        name: e.target.value,
                      }))
                    }
                    placeholder={t("profile.classes.placeholders.name")}
                    disabled={classSaving}
                    required
                  />
                </div>
              </div>

              <div className="profile-field">
                <label>{t("profile.classes.fields.students")}</label>
                {lessonsLoading ? (
                  <p className="profile-muted">
                    {t("profile.common.loadingData")}
                  </p>
                ) : teacherRosterStudents.length ? (
                  <div className="profile-grid">
                    {teacherRosterStudents.map((student) => {
                      const checked = (classForm.student_ids || []).some(
                        (id) => String(id) === String(student.id),
                      );
                      return (
                        <label
                          key={student.id}
                          className="profile-card"
                          style={{
                            display: "flex",
                            gap: "0.75rem",
                            alignItems: "center",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleClassStudent(student.id)}
                            disabled={classSaving}
                          />
                          <span style={{ fontWeight: 800 }}>
                            {student.full_name}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <p className="profile-empty">
                    {t("profile.classes.emptyRoster")}
                  </p>
                )}
              </div>

              {classesError && <p className="profile-error">{classesError}</p>}

              <div className="profile-actions">
                <button
                  type="submit"
                  className="btn primary"
                  disabled={classSaving}
                >
                  {classSaving
                    ? t("profile.actions.saving")
                    : editingClassId
                      ? t("profile.classes.actions.update")
                      : t("profile.classes.actions.add")}
                </button>
                {editingClassId && (
                  <button
                    type="button"
                    className="profile-button profile-button--ghost"
                    onClick={resetClassForm}
                    disabled={classSaving}
                  >
                    {t("profile.actions.cancelEdit")}
                  </button>
                )}
                <button
                  type="button"
                  className="profile-button profile-button--ghost"
                  onClick={loadClasses}
                  disabled={classesLoading || classSaving}
                >
                  {t("profile.actions.refresh")}
                </button>
              </div>
            </form>

            {classesLoading ? (
              <p className="profile-muted">{t("profile.classes.loading")}</p>
            ) : classGroups.length ? (
              <div className="profile-grid">
                {classGroups.map((group) => (
                  <article className="profile-card" key={group.id}>
                    <h3 className="profile-card__title">{group.name}</h3>
                    <p className="profile-muted">
                      {t("profile.classes.studentsCount", {
                        count: (group.student_ids || []).length,
                      })}
                    </p>
                    {Array.isArray(group.students) && group.students.length ? (
                      <p className="profile-muted">
                        {group.students
                          .map((s) => s.full_name)
                          .join(listSeparator)}
                      </p>
                    ) : (
                      <p className="profile-muted">
                        {t("profile.classes.noStudentsSelected")}
                      </p>
                    )}
                    <div className="profile-actions">
                      <button
                        type="button"
                        className="profile-button profile-button--ghost"
                        onClick={() => handleClassEdit(group)}
                      >
                        {t("profile.actions.edit")}
                      </button>
                      <button
                        type="button"
                        className="profile-button profile-button--danger"
                        onClick={() => handleClassDelete(group.id)}
                      >
                        {t("profile.actions.delete")}
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <p className="profile-empty">{t("profile.classes.empty")}</p>
            )}
          </section>
        )}

        {!viewingStudentProfile && (isTeacher || isParent) && (
          <section className="profile-section profile-lessons">
            <header className="profile-section__head">
              <div>
                <h2>{t("profile.lessons.title")}</h2>
                <p className="profile-muted">{t("profile.lessons.subtitle")}</p>
              </div>
            </header>
            {lessonsLoading ? (
              <p className="profile-muted">{t("profile.common.loadingData")}</p>
            ) : lessons.length ? (
              <>
                <div className="profile-grid">
                  <div className="profile-card">
                    <span className="profile-tag">
                      {t("profile.lessons.stats.total")}
                    </span>
                    <strong>{lessonTotals.total}</strong>
                  </div>
                  <div className="profile-card">
                    <span className="profile-tag">
                      {t("profile.lessons.stats.completed")}
                    </span>
                    <strong>{lessonTotals.completed}</strong>
                  </div>
                  <div className="profile-card">
                    <span className="profile-tag">
                      {t("profile.lessons.stats.cancelled")}
                    </span>
                    <strong>{lessonTotals.cancelled}</strong>
                  </div>
                </div>
                {upcomingLessons.length ? (
                  <div className="profile-grid">
                    {upcomingLessons.map((lesson) => (
                      <article className="profile-card" key={lesson.id}>
                        <h3 className="profile-card__title">{lesson.title}</h3>
                        <p className="profile-muted">
                          {t("profile.lessons.labels.scheduledAt", {
                            datetime: new Date(
                              lesson.scheduled_at,
                            ).toLocaleString(locale),
                          })}
                        </p>
                        <p className="profile-muted">
                          {t("profile.lessons.labels.student", {
                            name:
                              lesson.student?.full_name ||
                              t("profile.common.unknown"),
                          })}
                        </p>
                        <p className="profile-muted">
                          {t("profile.lessons.labels.duration", {
                            minutes: lesson.duration_minutes,
                          })}
                        </p>
                      </article>
                    ))}
                  </div>
                ) : (
                  <p className="profile-empty">
                    {t("profile.overview.noUpcomingLessons")}
                  </p>
                )}
              </>
            ) : (
              <p className="profile-empty">{t("profile.lessons.empty")}</p>
            )}
          </section>
        )}

        {!viewingStudentProfile && isAdmin && (
          <section className="profile-section profile-users">
            <header className="profile-section__head">
              <div>
                <h2>{t("profile.users.title")}</h2>
                <p className="profile-muted">{t("profile.users.subtitle")}</p>
              </div>
            </header>

            <div className="profile-grid">
              <div className="profile-field">
                <label htmlFor="users-role-filter">
                  {t("profile.users.filters.role")}
                </label>
                <select
                  id="users-role-filter"
                  value={roleFilter}
                  onChange={(event) => {
                    setUsersPage(1);
                    setRoleFilter(event.target.value);
                  }}
                  disabled={usersLoading}
                >
                  <option value="all">
                    {t("profile.users.filters.allRoles")}
                  </option>
                  {ROLE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {t(option.labelKey)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="profile-field">
                <label htmlFor="users-search">
                  {t("profile.users.filters.search")}
                </label>
                <div className="profile-actions">
                  <input
                    id="users-search"
                    type="search"
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    placeholder={t("profile.users.placeholders.search")}
                    disabled={usersLoading}
                  />
                  <button
                    type="button"
                    className="profile-button profile-button--ghost"
                    onClick={() => {
                      setUsersPage(1);
                      setSearchTerm(searchInput.trim());
                    }}
                    disabled={usersLoading}
                  >
                    {t("profile.users.actions.search")}
                  </button>
                  <button
                    type="button"
                    className="profile-button profile-button--ghost"
                    onClick={() => {
                      setUsersPage(1);
                      setSearchInput("");
                      setSearchTerm("");
                    }}
                    disabled={usersLoading}
                  >
                    {t("profile.users.actions.reset")}
                  </button>
                </div>
              </div>
            </div>

            {usersError && <p className="profile-error">{usersError}</p>}

            {usersLoading ? (
              <p className="profile-muted">{t("profile.users.loading")}</p>
            ) : visibleUsers.length ? (
              <div className="profile-info">
                <p>
                  {t("profile.users.summary", {
                    total: usersCount ?? visibleUsers.length,
                    admins: roleCounts.admin || 0,
                    teachers: roleCounts.teacher || 0,
                    parents: roleCounts.parent || 0,
                  })}
                </p>
              </div>
            ) : (
              <p className="profile-empty">{t("profile.users.empty")}</p>
            )}

            {visibleUsers.length > 0 && (
              <>
                <table className="profile-table">
                  <thead>
                    <tr>
                      <th>{t("profile.users.table.email")}</th>
                      <th>{t("profile.users.table.name")}</th>
                      <th>{t("profile.users.table.role")}</th>
                      <th>{t("profile.users.table.status")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleUsers.map((item) => (
                      <tr key={item.id}>
                        <td>{item.email}</td>
                        <td>{item.full_name}</td>
                        <td>
                          <select
                            value={item.role}
                            onChange={(event) =>
                              handleRoleUpdate(item.id, event.target.value)
                            }
                          >
                            {ROLE_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {t(option.labelKey)}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <span className={roleBadgeClass(item.role)}>
                            {roleLabel(item.role) || item.role}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <Pagination
                  page={usersPage}
                  totalPages={usersTotalPages}
                  dir={language === "ar" ? "rtl" : "ltr"}
                  disabled={usersLoading}
                  onPageChange={(nextPage) => setUsersPage(nextPage)}
                />
              </>
            )}
          </section>
        )}
      </div>
    </div>
  );

  return effectiveRole === "student" ? (
    <StudentShell>{page}</StudentShell>
  ) : (
    page
  );
}
